import React from 'react';
import { PRICING } from '../pricing';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleIcon from '@mui/icons-material/People';
import LanIcon from '@mui/icons-material/Lan';
import CodeIcon from '@mui/icons-material/Code';
import SyncIcon from '@mui/icons-material/Sync';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LayersIcon from '@mui/icons-material/Layers';
import BuildIcon from '@mui/icons-material/Build';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

export const angular = {
    title: "Angular Mastery",
    subtitle: "Enterprise Architecture",
    icon: <CodeIcon sx={{ fontSize: 40, color: '#dd0031' }} />,
    description: "Architect high-performance enterprise applications. Curriculum sourced directly from real-world FAANG interview experiences.",
    features: [
      "Custom Form Orchestration",
      "Advanced CDK & SSR Scale",
      "Hydration & PWA Persistence",
      "Based on Real Interviews"
    ],
    price: PRICING.angular.price,
    mrp: PRICING.angular.mrp,
    topics: [
      {
        id: "angular_fundamentals",
        title: "Angular Fundamentals",
        desc: "Master templates, data binding, directives, and standalone components.",
        badge: "Basics",
        icon: <DashboardIcon />,
        group: "Angular Fundamentals",
        problems: [
          { id: "angular_comp_model", title: "Standalone Components & Templates", badge: "Core", fileUrl: "/documents/angular/angular_comp_model.md" },
          { id: "angular_directives", title: "Structural & Attribute Directives", badge: "Logic", fileUrl: "/documents/angular/angular_directives.md" },
          { id: "angular_pipes", title: "Pipes & Data Transformation", badge: "Transform", fileUrl: "/documents/angular/angular_pipes.md" },
          { id: "angular_data_binding", title: "Two-way Binding & Input/Output", badge: "Data", fileUrl: "/documents/angular/angular_data_binding.md" },
          { id: "angular_view_queries", title: "View & Content Queries", badge: "Queries", fileUrl: "/documents/angular/angular_view_queries.md" },
          { id: "angular_content_projection", title: "Content Projection & Slots", badge: "Design", fileUrl: "/documents/angular/angular_content_projection.md" },
          { id: "angular_services_basics", title: "Services & DI Fundamentals", badge: "Logic", fileUrl: "/documents/angular/angular_services_basics.md" },
          { id: "angular_forms", title: "Reactive & Template Forms", badge: "Forms", fileUrl: "/documents/angular/angular_forms.md" }
        ]
      },
      {
        id: "angular_lifecycle_mastery",
        title: "Lifecycle Mastery",
        desc: "Deep dive into the sequence, triggers, and best practices of Angular lifecycle hooks.",
        badge: "Deep Dive",
        icon: <SyncIcon />,
        group: "Component Lifecycle",
        problems: [
          { id: "angular_lifecycle_init", title: "OnInit & OnDestroy: The Setup", badge: "Core", fileUrl: "/documents/angular/angular_lifecycle_init.md" },
          { id: "angular_lifecycle_changes", title: "OnChanges & DoCheck Mastery", badge: "Detection", fileUrl: "/documents/angular/angular_lifecycle_changes.md" },
          { id: "angular_lifecycle_view", title: "AfterView vs AfterContent", badge: "DOM", fileUrl: "/documents/angular/angular_lifecycle_view.md" },
          { id: "angular_lifecycle_guide", title: "Complete Sequence Guide", badge: "Guide", fileUrl: "/documents/angular/angular_lifecycle.md" }
        ]
      },
      {
        id: "angular_rxjs_reactive",
        title: "RxJS & Reactive Streams",
        desc: "Master asynchronous streams, operators, and state management using RxJS.",
        badge: "Reactive",
        icon: <SyncAltIcon />,
        group: "Reactive Programming",
        problems: [
          { id: "angular_rxjs_observables", title: "Observables & Subscriptions", badge: "Core", fileUrl: "/documents/angular/angular_rxjs_observables.md" },
          { id: "angular_rxjs_operators", title: "Essential Operators: The Big Three", badge: "Operators", fileUrl: "/documents/angular/angular_rxjs_operators.md" },
          { id: "angular_rxjs_subjects", title: "Subjects & BehaviorSubjects", badge: "State", fileUrl: "/documents/angular/angular_rxjs_subjects.md" },
          { id: "angular_rxjs_mapping", title: "Higher-order Mapping Operators", badge: "Advanced", fileUrl: "/documents/angular/angular_rxjs_mapping.md" },
          { id: "angular_rxjs_errors", title: "Error Handling & Retry Logic", badge: "Logic", fileUrl: "/documents/angular/angular_rxjs_errors.md" }
        ]
      },
      {
        id: "angular_core",
        title: "Core Mechanics: Signals & Ivy",
        desc: "Deep dive into the modern reactivity system and the Ivy rendering engine.",
        badge: "Expert",
        icon: <LayersIcon />,
        group: "Angular Core",
        problems: [
          { id: "angular_signals", title: "Angular Signals Under the Hood", badge: "Reactivity", fileUrl: "/documents/angular/angular_signals.md" },
          { id: "angular_ivy", title: "Ivy Engine & AOT Compilation", badge: "Expert", fileUrl: "/documents/angular/angular_ivy.md" },
          { id: "angular_change_detection", title: "Change Detection vs Zoneless", badge: "Perf", fileUrl: "/documents/angular/angular_change_detection.md" },
          { id: "angular_http", title: "Advanced HTTP & Interceptors", badge: "Network", fileUrl: "/documents/angular/angular_http.md" }
        ]
      },
      {
        id: "angular_advanced",
        title: "Advanced Architecture",
        desc: "Master Dependency Injection, Routing, and Scaling large apps.",
        badge: "Architect",
        icon: <LanIcon />,
        group: "Advanced Architecture",
        problems: [
          { id: "angular_di_patterns", title: "Hierarchical Dependency Injection", badge: "DI", fileUrl: "/documents/angular/angular_di_patterns.md" },
          { id: "angular_routing_guards", title: "Functional Routing & State Guards", badge: "Router", fileUrl: "/documents/angular/angular_routing_guards.md" },
          { id: "angular_micro_frontends", title: "Angular Micro-Frontends (MFEs)", badge: "Scale", fileUrl: "/documents/angular/angular_micro_frontends.md" }
        ]
      },
      {
        id: "angular_performance_scale",
        title: "Performance & Scale",
        desc: "Master SSR, Hydration, and Offline persistence for enterprise-grade speed.",
        badge: "Perf",
        icon: <SpeedIcon />,
        group: "Performance & Scale",
        problems: [
          { id: "angular_perf_optimization", title: "Hydration & Deferrable Views", badge: "Perf", fileUrl: "/documents/angular/angular_perf_optimization.md" },
          { id: "angular_advanced_ssr", title: "Advanced SSR & TransferState", badge: "SSR", fileUrl: "/documents/angular/angular_advanced_ssr.md" },
          { id: "angular_pwa", title: "PWA & Offline Native Persistence", badge: "PWA", fileUrl: "/documents/angular/angular_pwa.md" }
        ]
      },
      {
        id: "angular_enterprise_eng",
        title: "Enterprise Engineering",
        desc: "Build professional-grade forms and custom UI components.",
        badge: "Pro",
        icon: <FlashOnIcon />,
        group: "Enterprise Engineering",
        problems: [
          { id: "angular_cva", title: "Custom Controls: ControlValueAccessor", badge: "Deep", fileUrl: "/documents/angular/angular_cva.md" },
          { id: "angular_cdk", title: "CDK: Overlays, Portals & A11y", badge: "Expert", fileUrl: "/documents/angular/angular_cdk.md" },
          { id: "angular_i18n", title: "i18n: Globalization at Scale", badge: "Global", fileUrl: "/documents/angular/angular_i18n.md" }
        ]
      },
      {
        id: "angular_quality",
        title: "Quality Assurance",
        desc: "Master the art of unit, integration, and end-to-end testing.",
        badge: "Test",
        icon: <BuildIcon />,
        group: "Quality Assurance",
        problems: [
          { id: "angular_testing", title: "Unit & Integration Testing", badge: "Test", fileUrl: "/documents/angular/angular_testing.md" },
          { id: "angular_e2e", title: "Modern E2E: Cypress & Playwright", badge: "E2E", fileUrl: "/documents/angular/angular_e2e.md" }
        ]
      },
      {
        id: "angular_enterprise",
        title: "Enterprise Architecture",
        desc: "Master global state management, design systems, and security.",
        badge: "Expert",
        icon: <AdminPanelSettingsIcon />,
        group: "Enterprise Architecture",
        problems: [
          { id: "angular_state_management", title: "Global State: NgRx & SignalStore", badge: "State", fileUrl: "/documents/angular/angular_state_management.md" },
          { id: "angular_security", title: "Production Security & CSP", badge: "Security", fileUrl: "/documents/angular/angular_security.md" },
          { id: "angular_animations", title: "Complexity & Animations DSL", badge: "UI", fileUrl: "/documents/angular/angular_animations.md" }
        ]
      },
      {
        id: "angular_interview_prep",
        title: "Interview Preparation",
        desc: "Master frequently asked questions for senior Angular roles.",
        badge: "Prep",
        icon: <PeopleIcon />,
        group: "Angular Practice",
        problems: [
          { id: "angular_qa_mastery", title: "Angular Interview Q&A", badge: "Core", fileUrl: "/documents/angular/angular_interview_questions.md" },
          { id: "angular_enterprise_scenarios", title: "Real-World Enterprise Scenarios", badge: "Senior", fileUrl: "/documents/angular/angular_enterprise_scenarios.md" }
        ]
      }
    ]
  };
